import React from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import {
  CoachesCoachingKind,
  EhrAppointmentSchedulingMode,
  EhrAppointmentStateKind,
  EhrProvider,
} from '@src/types'
import { mntChatConversationSelector, stressChatConversationSelector } from '@src/selectors/app'
import { useLazyScreenPreload } from '@src/navigation/utils'
import {
  ehrAppointmentSchedulingInfoSelector,
  pastEhrAppointmentsSelector,
  priorityOrderedCoachAssignmentsSelector,
} from '../../models/nutritionistHub.selectors'
import { MntCard } from './MntCard'
import { StressCard } from './StressCard'

export const CareTeamCard = () => {
  const styles = useStyleSheet(themedStyle)

  const { mnt, stressManagement } = useSelector(ehrAppointmentSchedulingInfoSelector) || {}
  const allCoachAssignments = useSelector(priorityOrderedCoachAssignmentsSelector) || []
  const mntConversation = useSelector(mntChatConversationSelector)
  const stressConversation = useSelector(stressChatConversationSelector)
  const pastAppointments = useSelector(pastEhrAppointmentsSelector)

  useLazyScreenPreload('CoachInfoModal')

  const hasOccurredAppointmentWithCoach = (coachId?: string) =>
    pastAppointments.some((appointment) => {
      // The type is non-nullable, but there are cases where it is undefined and crashes the simulator.
      const providers = appointment.providers as EhrProvider[] | undefined
      const hasMatchingCoach = providers?.some((provider) => provider.coach?.coachId === coachId)

      return hasMatchingCoach && appointment.lastState.kind === EhrAppointmentStateKind.Occurred
    })

  const canScheduleMnt = !!mnt && mnt.schedulingMode !== EhrAppointmentSchedulingMode.None
  const mntCoachAssignment = allCoachAssignments.find(
    (assignment) => assignment.coachingKind.key === CoachesCoachingKind.Mnt,
  )
  const hasMntCoach = !!mntCoachAssignment?.coach
  const canSwitchMntCoach = hasOccurredAppointmentWithCoach(mntCoachAssignment?.coach?.id)
  const showMntCard = hasMntCoach || canScheduleMnt
  const showMntUnreadMessage = mntConversation?.candidateRead === false && hasMntCoach

  const stressCoachAssignment = allCoachAssignments.find(
    (assignment) => assignment.coachingKind.key === CoachesCoachingKind.StressManagement,
  )
  const hasStressCoach = !!stressCoachAssignment?.coach
  const canScheduleStressManagement =
    !!stressManagement && stressManagement.schedulingMode !== EhrAppointmentSchedulingMode.None
  const canSwitchStressCoach = hasOccurredAppointmentWithCoach(stressCoachAssignment?.coach?.id)
  const showStressCard = hasStressCoach || canScheduleStressManagement
  const showStressNewChip = hasMntCoach && !hasStressCoach
  const showStressUnreadMessage = stressConversation?.candidateRead === false && hasStressCoach

  if (!showMntCard && !showStressCard) {
    return null
  }

  return (
    <View style={styles.container}>
      {showMntCard && (
        <MntCard
          coachAssignment={mntCoachAssignment}
          conversation={mntConversation}
          canSwitch={canSwitchMntCoach}
          hasUnreadMessage={showMntUnreadMessage}
          showPopularChip
        />
      )}
      {showMntCard && showStressCard && <View style={styles.separator} />}
      {showStressCard && (
        <StressCard
          coachAssignment={stressCoachAssignment}
          conversation={stressConversation}
          canSwitch={canSwitchStressCoach}
          hasUnreadMessage={showStressUnreadMessage}
          showNewChip={showStressNewChip}
        />
      )}
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    backgroundColor: 'theme.background.selected',
    borderColor: 'theme.surface.base1',
    borderRadius: 16,
    borderWidth: 2,
    marginBottom: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  separator: {
    height: 1,
    backgroundColor: 'theme.surface.base1',
    marginVertical: 16,
  },
})
